import { useLayoutEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { useReveal } from '../../../lib/useReveal'
import styles from './Feed.module.css'

/** One thing that reached the character. Assembled from the engine's `message` events. */
export interface FeedLine {
  id: string
  text: string
  /** narration · speech · body · interoception · ambient · establish-inline */
  channel: string
  speaker?: string
  /** True for the player's own will, echoed back into the column. */
  fromPlayer?: boolean
  /**
   * Word-by-word reveal duration. Non-zero only for spoken speech, where the
   * words genuinely take that long to say (doctrine-spoken-word-has-duration).
   * Everything else arrives whole.
   */
  revealMs: number
  /** Fictional time, shown when the stamp changes. */
  at?: string
}

/** A short line standing alone reads as a beat, and is centred. */
const isBeat = (text: string) => text.length <= 48 && !/[,;:]/.test(text)

function Line({ line, depth }: { line: FeedLine; depth: number }) {
  const text = useReveal(line.text, line.revealMs)

  // The arriving edge is brightest; older lines step back but never vanish.
  const opacity = Math.max(0.28, 1 - depth * 0.22)

  const cls = [
    styles.line,
    line.fromPlayer ? styles.player : '',
    line.channel === 'body' || line.channel === 'interoception' ? styles.body : '',
    line.channel === 'ambient' ? styles.ambient : '',
    !line.fromPlayer && line.channel === 'narration' && isBeat(line.text)
      ? styles.beat
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <p className={cls} style={{ '--depth-opacity': opacity } as CSSProperties}>
      {line.speaker && <span className={styles.speaker}>{line.speaker}</span>}
      {text}
    </p>
  )
}

const stampOf = (iso?: string) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export default function Feed({
  lines,
  thinking,
  endedReason,
}: {
  lines: FeedLine[]
  thinking: boolean
  endedReason?: string | null
}) {
  const ref = useRef<HTMLDivElement>(null)

  // Keep the arriving edge in view. Layout effect so it lands before paint.
  useLayoutEffect(() => {
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [lines.length, thinking, endedReason])

  let lastStamp: string | null = null

  return (
    <div className={styles.column} ref={ref}>
      {lines.map((line, i) => {
        const stamp = stampOf(line.at)
        const showStamp = stamp !== null && stamp !== lastStamp
        if (stamp) lastStamp = stamp
        return (
          <div key={line.id}>
            {showStamp && <div className={styles.stamp}>{stamp}</div>}
            <Line line={line} depth={lines.length - 1 - i} />
          </div>
        )
      })}

      {thinking && <p className={styles.thinking}>the scene continues</p>}

      {endedReason && <p className={styles.ended}>— {endedReason} —</p>}
    </div>
  )
}
